import { TemplateField, TemplateStep } from "../../../types/types";
import { formatEnumString } from "../../../utils/utilMethods";
import { PayoutComponent } from "../../../utils/opportunities/outcomeRegistryHelpers";

export const EMPTY_VALUE = "-";

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonValue[]
  | { [key: string]: JsonValue };

export type JsonObject = { [key: string]: JsonValue };

export type Translator = (
  key: string,
  options?: { defaultValue?: string },
) => string;

interface FileLike {
  fileUrl?: string;
  url?: string;
  downloadUrl?: string;
  fileName?: string;
  name?: string;
}

type FieldOption = string | number | { label?: string; value: string | number };

export interface OutcomeData {
  label?: string;
  baseLocked?: boolean;
  baseIncluded?: boolean;
  selectionMode?: string;
  selectedComponents?: string[];
  bruto?: number;
  total?: number;
}

export interface OpportunityFormData {
  core?: JsonObject;
  components?: FinancialComponent[];
  [stepId: string]: unknown;
}

export const isBlank = (value: unknown): value is null | undefined | "" =>
  value === undefined || value === null || value === "";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export type ViewStep = TemplateStep & { outcomeRegistry?: unknown };

export interface FinancialComponent extends PayoutComponent {
  kind?: string;
  expenseDescription?: string;
  fundingStage?: string;
}

const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const FILE_EXTENSION_PATTERN =
  /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf|csv|zip|rar|7z)(?:$|[?#])/i;
const SHORT_ACRONYM_PATTERN = /^[A-Za-z0-9]{1,3}$/;

export const toTitleCase = (str: string): string => {
  const normalized = String(str || "")
    .replace(/[_-]+/g, " ")
    .trim();

  const readableText = /^[A-Z0-9\s]+$/.test(normalized)
    ? normalized.toLowerCase()
    : normalized.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();

  return readableText
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

export const safe = (val: unknown): string => {
  if (val === null || val === undefined) return EMPTY_VALUE;
  if (typeof val === "string" && val.trim() === "") return EMPTY_VALUE;
  return String(val);
};

export const flattenTranslationValues = (
  source: Record<string, unknown>,
  prefix = "",
): Record<string, string> =>
  Object.entries(source).reduce(
    (acc: Record<string, string>, [key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "string") {
        acc[value] = fullKey;
      } else if (value && typeof value === "object") {
        Object.assign(
          acc,
          flattenTranslationValues(value as Record<string, unknown>, fullKey),
        );
      }

      return acc;
    },
    {},
  );

const isEnumLikeString = (value: string): boolean => {
  const trimmed = value.trim();
  return (
    /^[A-Z0-9_]+$/.test(trimmed) ||
    /^[A-Za-z0-9]+(?:_[A-Za-z0-9]+)+$/.test(trimmed)
  );
};

export const shouldFormatChoiceValue = (value: string, key?: string): boolean =>
  isEnumLikeString(value) || (key ? /policy$/i.test(key) : false);

export const formatChoiceText = (value: string, t?: Translator): string => {
  const trimmed = value.trim();

  if (t) {
    const translated = t(`field_values.${trimmed.toLowerCase()}`, {
      defaultValue: "",
    });
    if (translated) return translated;
  }

  if (/^[A-Z0-9_]+$/.test(trimmed) || trimmed.includes("_")) {
    return formatEnumString(trimmed) || toTitleCase(trimmed);
  }
  return toTitleCase(trimmed);
};

export const formatSportLabel = (value: string): string => {
  const trimmed = value.trim();
  if (SHORT_ACRONYM_PATTERN.test(trimmed)) return trimmed.toUpperCase();
  return toTitleCase(trimmed);
};

export const formatWeightClassLabel = (value: string): string =>
  toTitleCase(value);

export const isIsoDateString = (value: string): boolean =>
  ISO_DATE_PATTERN.test(value);

export const formatDisplayDate = (value: string): string => {
  const dateOnly = DATE_ONLY_PATTERN.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatAmount = (value: number, currencySymbol: string): string => {
  const amount = new Intl.NumberFormat("en-US", {
    useGrouping: false,
    maximumFractionDigits: 2,
  }).format(value);
  return currencySymbol ? `${amount} ${currencySymbol}` : amount;
};

export const getFieldOptionLabel = (
  field: TemplateField | undefined,
  value: unknown,
  t?: Translator,
): string | null => {
  if (!field?.options?.length || value === null || value === undefined) {
    return null;
  }

  const options: FieldOption[] = field.options;
  const option = options.find((item) => {
    const optionValue = typeof item === "object" ? item.value : item;
    return String(optionValue) === String(value);
  });

  if (!option) return null;
  if (typeof option === "object") {
    return option.label || formatChoiceText(String(option.value), t);
  }
  return formatChoiceText(String(option), t);
};

export const shouldRenderNestedValue = (
  value: unknown,
  field?: TemplateField,
): boolean =>
  typeof value === "object" &&
  value !== null &&
  !(Array.isArray(value) && !!field?.options?.length);

export const getFileUrl = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (isRecord(value)) {
    const file = value as FileLike;
    return file.fileUrl || file.url || file.downloadUrl || "";
  }
  return "";
};

export const isFileValue = (value: unknown): boolean => {
  const url = getFileUrl(value);
  return /^https?:\/\//i.test(url) && FILE_EXTENSION_PATTERN.test(url);
};

const decodeFileSegment = (segment: string | undefined): string => {
  if (!segment) return "";
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export const getFileName = (value: unknown, fallback: string): string => {
  const file = isRecord(value) ? (value as FileLike) : undefined;
  if (file?.fileName || file?.name) {
    return file.fileName || file.name || fallback;
  }

  const url = getFileUrl(value);
  let rawName: string | undefined;

  try {
    rawName = new URL(url).pathname.split("/").filter(Boolean).pop();
  } catch {
    rawName = url.split("/").pop()?.split("?")[0];
  }

  return decodeFileSegment(rawName) || fallback;
};

export const getFileExtension = (fileName: string, url = ""): string => {
  const fromName = fileName.includes(".") ? fileName.split(".").pop() : "";
  if (fromName) return fromName.toUpperCase();

  const fromUrl = FILE_EXTENSION_PATTERN.exec(url);
  return fromUrl ? fromUrl[1].toUpperCase() : "FILE";
};

export const getStepFields = (step: TemplateStep): TemplateField[] =>
  Array.isArray(step.fields) ? step.fields : [];

export const getPolicyOutcomes = (
  stepData: unknown,
): Record<string, OutcomeData> => {
  if (!isRecord(stepData)) return {};
  return (
    isRecord(stepData.outcomes) ? stepData.outcomes : stepData
  ) as Record<string, OutcomeData>;
};

export const isPenaltyComponent = (component?: FinancialComponent): boolean =>
  String(component?.kind || "").toLowerCase() === "penalty";

export const getSignedComponentAmount = (
  component: FinancialComponent | undefined,
  amount: number,
): number => (isPenaltyComponent(component) ? -Math.abs(amount) : amount);

export const findComponentByType = (
  components: FinancialComponent[],
  componentType: string,
): FinancialComponent | undefined =>
  components.find(
    (item) =>
      item?.componentType &&
      String(item.componentType).toLowerCase() ===
        String(componentType).toLowerCase(),
  );
